import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useCreateAccountGroupMutation } from '@/core/api'
import { useGetAdminAccountsQuery } from '@/core/services/admin'
import { useGetPoliciesQuery } from '@/core/services/policy'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isStaff } from '@/utils/type-guards'

import GroupForm from '../components/GroupForm'

export default function CreateGroupForm() {
  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: accounts } = useGetAdminAccountsQuery()
  const { data: policies } = useGetPoliciesQuery()
  const [createAccountGroup, { isLoading: isCreatingAccountGroup }] = useCreateAccountGroupMutation()

  const policiesData = useMemo(
    () =>
      policies?.map((policy) => ({
        id: policy._id,
        name: policy.name,
        description: policy.description,
      })),
    [policies]
  )

  const accountsData = useMemo(
    () =>
      accounts?.map((account) => ({
        id: account._id,
        name: isStaff(account) ? `${account.userInfo.name} ${account.userInfo.surname}` : account.name,
        status: isStaff(account) ? account.status : 'API KEY',
      })),
    [accounts]
  )

  return (
    <GroupForm
      accounts={accountsData}
      isSubmitting={isCreatingAccountGroup}
      onSubmit={async (values) => {
        if (!authUser?.establishment) return
        try {
          await createAccountGroup({
            accounts: values.accounts,
            establishment: authUser.establishment._id,
            name: values.name,
            policies: values.policies,
          }).unwrap()
          navigate(location.pathname.replace('/create', ''))
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.accountGroup.create.error')), { variant: 'error' })
        }
      }}
      policies={policiesData}
    />
  )
}
